import { useState, useEffect } from "react"
import { BookOpen, Image as ImageIcon, X, Save, Loader2 } from "lucide-react"
import { Button } from "../ui/button"
import ImageUpload from "../ImageUpload"

export default function CourseImageForm({
  course,
  onSave,
  onCancel
}) {
  const [thumbnail, setThumbnail] = useState(null)
  const [thumbnailFile, setThumbnailFile] = useState(null)
  const [banner, setBanner] = useState(null)
  const [bannerFile, setBannerFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (course) {
      setThumbnail(course.thumbnail || null)
      setThumbnailFile(null)
      setBanner(course.banner || null)
      setBannerFile(null)
    }
  }, [course])

  const handleThumbnailSelect = (file) => {
    if (file instanceof File) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setThumbnailFile(file)
        setThumbnail(e.target.result)
      }
      reader.readAsDataURL(file)
    } else {
      setThumbnail(file)
      setThumbnailFile(null)
    }
    // Clear error
    if (errors.thumbnail) {
      setErrors(prev => ({ ...prev, thumbnail: null }))
    }
  }

  const handleBannerSelect = (file) => {
    if (file instanceof File) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setBannerFile(file)
        setBanner(e.target.result)
      }
      reader.readAsDataURL(file)
    } else {
      setBanner(file)
      setBannerFile(null)
    }
    // Clear error
    if (errors.banner) {
      setErrors(prev => ({ ...prev, banner: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const data = {}
      
      // Ajouter thumbnail si un fichier a été sélectionné
      if (thumbnailFile instanceof File) {
        data.thumbnailFile = thumbnailFile
      }
      // Note: Si thumbnail est une URL (string) et non un fichier, 
      // on peut l'envoyer directement (pour support des URLs externes)
      else if (thumbnail && typeof thumbnail === 'string' && !thumbnail.startsWith('data:')) {
        data.thumbnail = thumbnail
      }
      
      // Ajouter banner si un fichier a été sélectionné
      if (bannerFile instanceof File) {
        data.bannerFile = bannerFile
      }
      // Note: Si banner est une URL (string) et non un fichier,
      // on peut l'envoyer directement (pour support des URLs externes)
      else if (banner && typeof banner === 'string' && !banner.startsWith('data:')) {
        data.banner = banner
      }
      
      // Vérifier qu'au moins une image a été modifiée
      if (!data.thumbnailFile && !data.bannerFile && !data.thumbnail && !data.banner) {
        setErrors({ thumbnail: 'Veuillez sélectionner au moins une image à modifier' })
        setSaving(false)
        return
      }
      
      await onSave(data)
    } catch (error) {
      console.error('Error saving course image:', error)
      setErrors({ thumbnail: 'Erreur lors de la sauvegarde de l\'image' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <ImageIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Modifier l'image du cours</h2>
              <p className="text-sm text-gray-600 mt-1">{course?.title || 'Cours'}</p>
            </div>
          </div>
          {onCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="h-8"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
          {/* Thumbnail Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Miniature du cours
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                <ImageUpload
                  className="w-full max-w-md mx-auto"
                  currentImage={thumbnail}
                  onImageSelect={handleThumbnailSelect}
                />
                {thumbnail && (
                  <div className="w-64 h-48 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
                    <img
                      src={thumbnail || (thumbnailFile instanceof File ? URL.createObjectURL(thumbnailFile) : '')}
                      alt="Aperçu miniature"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
              {errors.thumbnail && (
                <p className="text-sm text-red-600">{errors.thumbnail}</p>
              )}
              <p className="text-sm text-gray-500">
                Format recommandé : JPG, PNG. Taille recommandée : 1200x675px
              </p>
            </div>
          </div>

          {/* Banner Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Bannière du cours
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                <ImageUpload
                  className="w-full max-w-md mx-auto"
                  currentImage={banner}
                  onImageSelect={handleBannerSelect}
                />
                {banner && (
                  <div className="w-full max-w-2xl h-48 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
                    <img
                      src={banner || (bannerFile instanceof File ? URL.createObjectURL(bannerFile) : '')}
                      alt="Aperçu bannière"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
              {errors.banner && (
                <p className="text-sm text-red-600">{errors.banner}</p>
              )}
              <p className="text-sm text-gray-500">
                Format recommandé : JPG, PNG. Taille recommandée : 1920x1080px
              </p>
            </div>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
          >
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={saving}
          className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder l'image
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

